import { Settings } from '@/constants/settings';
import { Lang } from '@/store/player/types';
import PlayerStore from '@/store/player';
import { Tr } from '@/i18n/i18n';
import { StringUtils } from '@/utils/string-utils';

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

type PluralForm = 'one' | 'few' | 'many';

function padTimePart(value: number): string {
  return String(value).padStart(2, '0');
}

function formatHours(date: Date): string {
  return `${padTimePart(date.getHours())}:${padTimePart(date.getMinutes())}`;
}

function formatDate(date: Date): string {
  return `${padTimePart(date.getDate())}.${padTimePart(date.getMonth() + 1)}.${date.getFullYear()}`;
}

function pluralForm(value: number): PluralForm {
  if (Settings.LANG_TYPE !== Lang.RU) {
    return value === 1 ? 'one' : 'many';
  }
  const mod100 = value % 100;
  const mod10 = value % 10;

  if (mod100 >= 11 && mod100 <= 14) {
    return 'many';
  }
  if (mod10 === 1) {
    return 'one';
  }
  if (mod10 >= 2 && mod10 <= 4) {
    return 'few';
  }
  return 'many';
}

function formatAgo(unitKey: string, value: number): string {
  return StringUtils.formatString(Tr(`chat.time.${unitKey}.${pluralForm(value)}`), value);
}

function getStartOfDayTime(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

export function formatChatSentTime(messageTime?: number, fallbackSentAt: string = ''): string {
  if (!messageTime || !Number.isFinite(messageTime)) {
    return fallbackSentAt;
  }

  const sentDate = new Date(messageTime);
  if (Number.isNaN(sentDate.getTime())) {
    return fallbackSentAt;
  }

  const now = new Date(Date.now() + PlayerStore.getTimeDeltaMs());
  const diffMs = Math.max(now.getTime() - sentDate.getTime(), 0);
  const dayDiff = Math.round((getStartOfDayTime(now) - getStartOfDayTime(sentDate)) / DAY_MS);

  if (dayDiff > 1) {
    return StringUtils.formatString(Tr('chat.time.date.at'), formatDate(sentDate), formatHours(sentDate));
  }

  if (dayDiff === 1) {
    return StringUtils.formatString(Tr('chat.time.yesterday.at'), formatHours(sentDate));
  }

  if (diffMs < MINUTE_MS) {
    return Tr('chat.time.just.now');
  }

  if (diffMs < HOUR_MS) {
    return formatAgo('minutes', Math.floor(diffMs / MINUTE_MS));
  }

  if (diffMs < 4 * HOUR_MS) {
    return formatAgo('hours', Math.floor(diffMs / HOUR_MS));
  }

  return StringUtils.formatString(Tr('chat.time.at'), formatHours(sentDate));
}
